import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'

const STRENGTH_CRITERIA = [
    { regex: /[a-z]/, label: 'error_password_least_one_lowercase' },
    { regex: /[A-Z]/, label: 'error_password_least_one_uppercase' },
    { regex: /[0-9]/, label: 'error_password_least_one_number' },
    { regex: /[!@#$%^&*(),.?":{}|<>+\-/_=]/, label: 'error_password_least_one_special' }
]

const VALIDATION_RULES = [
    { regex: /^[A-Za-z0-9!@#$%^&*()\-+=[\]{}|\\:;'"<>,.?/]+$/, label: 'error_password_format' },
    { regex: /^\S*$/, label: 'error_password_must_not_contain_spaces' }
]

const STRENGTH_LEVELS = [
    { background: '#F2F4F7', text: 'password_strength' },
    { background: '#F44336', text: 'very_weak' },
    { background: '#FFF422', text: 'weak' },
    { background: '#4CAF50', text: 'good' },
    { background: '#4CAF50', text: 'strong' },
    { background: '#4CAF50', text: 'strong' }
]

const ALL_RULES = [{ regex: /.{8,}/, label: 'error_password_length' }, ...STRENGTH_CRITERIA, ...VALIDATION_RULES]

const TOTAL_BARS = 5

export const validatePasswordStrength = (password: string): string[] => ALL_RULES.filter(({ regex }) => !regex.test(password)).map(({ label }) => label)

export interface PasswordStrengthBarProps {
    password: string
    showDescription?: boolean
}

export const PasswordStrengthBar = ({ password, showDescription = true }: PasswordStrengthBarProps) => {
    const { translate } = useTranslate()

    const { strength, background, text } = useMemo(() => {
        let score = 0
        if (password.length >= 8) score++
        for (const { regex } of STRENGTH_CRITERIA) {
            if (regex.test(password)) score++
        }
        const level = Math.min(score, STRENGTH_LEVELS.length - 1)
        const entry = STRENGTH_LEVELS[level] ?? STRENGTH_LEVELS[0]!
        return {
            strength: score,
            background: entry.background,
            text: entry.text
        }
    }, [password])

    return (
        <Stack sx={{ width: '100%', mt: 1 }} data-testid="password-strength-bar-stack-typography-1">
            <Stack direction="row" gap="5px" data-testid="password-strength-bar-stack">
                {Array.from({ length: TOTAL_BARS }).map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 50,
                            background: i < strength ? background : '#F2F4F7',
                            transition: 'background 0.3s ease'
                        }}
                        data-testid="password-strength-bar-box"
                    />
                ))}
            </Stack>
            <Typography
                variant="body2"
                sx={{ fontSize: 10, fontWeight: 400, color: '#696969', textAlign: 'right', mt: '4px' }}
                data-testid="password-strength-bar-typography-1">
                {translate(text)}
            </Typography>
            {showDescription && (
                <Box
                    sx={{ mt: '20px', backgroundColor: '#E9F5FA', p: '16px 24px', borderRadius: 2 }}
                    data-testid="password-strength-bar-box-typography-your-password-must-be-contain">
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 14, fontWeight: 700, color: '#0B3B61', mb: '12px' }}
                        data-testid="password-strength-bar-typography-your-password-must-be-contain">
                        {translate('your_password_must_be_contain')}
                    </Typography>
                    {[...STRENGTH_CRITERIA, ...VALIDATION_RULES]
                        .map(c => c.label)
                        .map(item => (
                            <Stack key={item} direction="row" gap="5px" sx={{ pl: 1 }} data-testid="password-strength-bar-stack-typography-2">
                                <Typography variant="body2" sx={{ fontSize: 14, color: '#0B3B61' }} data-testid="password-strength-bar-typography-2">
                                    •
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: 14, fontWeight: 400, color: '#0B3B61' }}
                                    data-testid="password-strength-bar-typography-3">
                                    {translate(item)}
                                </Typography>
                            </Stack>
                        ))}
                </Box>
            )}
        </Stack>
    )
}
